/** MOTLibrary + MOTGlyphRenderer, ported 1:1 (same proportions, colours and legends). */
import { css, SYSTEM, withAlpha } from "./colors.js";
import { type Ctx, type Rect, inset, maxX, maxY, midX, midY, minX, minY, rect } from "./ctx.js";
import { textAt } from "./text.js";

export type MOTGlyph =
  | { g: "warningDiamond" } | { g: "regulatoryRect" } | { g: "prohibitionRect" } | { g: "stopOctagon" } | { g: "guideRect" }
  | { g: "cone" } | { g: "drum" } | { g: "barricadeX" } | { g: "barricadeRails"; n: number } | { g: "verticalPanel" }
  | { g: "tubularMarker" } | { g: "delineator" } | { g: "arrowBoard" } | { g: "arrowBoardCaution" } | { g: "messageBoard" }
  | { g: "truckAttenuator" } | { g: "workVehicle" } | { g: "temporarySignal" } | { g: "flaggerStation" }
  | { g: "pedestrianDetour"; arrowRight: boolean; bike: boolean } | { g: "pedestrianWarning" } | { g: "crossHereArrow"; right: boolean }
  | { g: "accessiblePanel" } | { g: "pedChannelizer" } | { g: "barricadeWithSign" } | { g: "curbRamp" } | { g: "detectableWarning" };

export interface MOTDefinition { symbol: string; displayName: string; mutcdCode: string; category: string; glyph: MOTGlyph; legend: string; defaultSize: [number, number] }

const sign: [number, number] = [74, 74], wideSign: [number, number] = [96, 60], pedSign: [number, number] = [96, 48], pedSignTall: [number, number] = [96, 72], detourPanel: [number, number] = [90, 72];
const G = (g: string, extra: object = {}) => ({ g, ...extra }) as MOTGlyph;
const D = (symbol: string, displayName: string, mutcdCode: string, category: string, glyph: MOTGlyph, legend: string, defaultSize: [number, number]): MOTDefinition =>
  ({ symbol, displayName, mutcdCode, category, glyph, legend, defaultSize });

export const MOT_CATEGORIES = ["Advance Warning", "Lane / Road Closure", "Regulatory", "Guide & Detour", "Pedestrian / Sidewalk", "Channelizing Devices", "Arrow Boards", "Variable Message Boards", "Flagging & Vehicles", "Work Area"] as const;

/** In `MOTSymbol.allCases` order. */
export const MOT_LIBRARY: readonly MOTDefinition[] = [
  D("roadWorkAhead", "Road Work Ahead", "W20-1", "Advance Warning", G("warningDiamond"), "ROAD\nWORK\nAHEAD", sign),
  D("oneLaneRoadAhead", "One Lane Road Ahead", "W20-4", "Advance Warning", G("warningDiamond"), "ONE LANE\nROAD\nAHEAD", sign),
  D("flagger", "Flagger Ahead", "W20-7", "Advance Warning", G("warningDiamond"), "FLAGGER\nAHEAD", sign),
  D("cone", "Cone", "—", "Channelizing Devices", G("cone"), "", [30, 52]),
  D("drum", "Drum", "—", "Channelizing Devices", G("drum"), "", [34, 52]),
  D("barricade", "Barricade", "—", "Channelizing Devices", G("barricadeX"), "", [80, 34]),
  D("arrowBoard", "Arrow Board", "—", "Arrow Boards", G("arrowBoard"), "", wideSign),
  D("variableMessageBoard", "Variable Message Board", "—", "Variable Message Boards", G("messageBoard"), "VMB", [100, 52]),
  D("workersAhead", "Workers Ahead", "W21-1", "Advance Warning", G("warningDiamond"), "WORKERS\nAHEAD", sign),
  D("shoulderWorkAhead", "Shoulder Work", "W21-5", "Advance Warning", G("warningDiamond"), "SHOULDER\nWORK", sign),
  D("utilityWorkAhead", "Utility Work Ahead", "W21-7", "Advance Warning", G("warningDiamond"), "UTILITY\nWORK\nAHEAD", sign),
  D("surveyCrewAhead", "Survey Crew", "W21-6", "Advance Warning", G("warningDiamond"), "SURVEY\nCREW", sign),
  D("laneEnds", "Lane Ends", "W9-1", "Advance Warning", G("warningDiamond"), "LANE\nENDS", sign),
  D("detourAhead", "Detour Ahead", "W20-2", "Advance Warning", G("warningDiamond"), "DETOUR\nAHEAD", sign),
  D("rightLaneClosedAhead", "Right Lane Closed Ahead", "W20-5R", "Lane / Road Closure", G("warningDiamond"), "RIGHT LANE\nCLOSED\nAHEAD", sign),
  D("leftLaneClosedAhead", "Left Lane Closed Ahead", "W20-5L", "Lane / Road Closure", G("warningDiamond"), "LEFT LANE\nCLOSED\nAHEAD", sign),
  D("roadClosedAhead", "Road Closed Ahead", "W20-3", "Lane / Road Closure", G("warningDiamond"), "ROAD\nCLOSED\nAHEAD", sign),
  D("roadClosed", "Road Closed", "R11-2", "Lane / Road Closure", G("regulatoryRect"), "ROAD CLOSED", [108, 44]),
  D("roadClosedThruTraffic", "Road Closed To Thru Traffic", "R11-4", "Lane / Road Closure", G("regulatoryRect"), "ROAD CLOSED\nTO\nTHRU TRAFFIC", [108, 64]),
  D("speedLimit", "Speed Limit", "R2-1", "Regulatory", G("regulatoryRect"), "SPEED\nLIMIT\n45", [58, 74]),
  D("doNotEnter", "Do Not Enter", "R5-1", "Regulatory", G("prohibitionRect"), "DO NOT\nENTER", [74, 74]),
  D("stop", "Stop", "R1-1", "Regulatory", G("stopOctagon"), "STOP", [68, 68]),
  D("keepRight", "Keep Right", "R4-7", "Regulatory", G("regulatoryRect"), "KEEP\nRIGHT", [58, 74]),
  D("endRoadWork", "End Road Work", "G20-2", "Guide & Detour", G("guideRect"), "END\nROAD WORK", [104, 52]),
  D("detourRight", "Detour (Right)", "M4-9R", "Guide & Detour", G("guideRect"), "DETOUR ▸", [104, 40]),
  D("detourLeft", "Detour (Left)", "M4-9L", "Guide & Detour", G("guideRect"), "◂ DETOUR", [104, 40]),
  D("detourMarker", "Detour Marker", "M4-8", "Guide & Detour", G("guideRect"), "DETOUR", [96, 40]),
  D("barricadeTypeI", "Type I Barricade", "—", "Channelizing Devices", G("barricadeRails", { n: 1 }), "", [60, 46]),
  D("barricadeTypeII", "Type II Barricade", "—", "Channelizing Devices", G("barricadeRails", { n: 2 }), "", [60, 52]),
  D("barricadeTypeIII", "Type III Barricade", "—", "Channelizing Devices", G("barricadeRails", { n: 3 }), "", [96, 58]),
  D("verticalPanel", "Vertical Panel", "—", "Channelizing Devices", G("verticalPanel"), "", [22, 56]),
  D("tubularMarker", "Tubular Marker", "—", "Channelizing Devices", G("tubularMarker"), "", [16, 50]),
  D("delineator", "Delineator", "—", "Channelizing Devices", G("delineator"), "", [16, 52]),
  D("arrowBoardCaution", "Arrow Board (Caution)", "—", "Arrow Boards", G("arrowBoardCaution"), "", wideSign),
  D("flaggerStation", "Flagger Station", "—", "Flagging & Vehicles", G("flaggerStation"), "", [44, 60]),
  D("truckMountedAttenuator", "Truck Mounted Attenuator", "—", "Flagging & Vehicles", G("truckAttenuator"), "TMA", [96, 46]),
  D("workVehicle", "Work Vehicle", "—", "Flagging & Vehicles", G("workVehicle"), "", [86, 44]),
  D("temporarySignal", "Temporary Signal", "—", "Flagging & Vehicles", G("temporarySignal"), "", [34, 74]),
  D("sidewalkClosed", "Sidewalk Closed", "R9-9", "Pedestrian / Sidewalk", G("regulatoryRect"), "SIDEWALK\nCLOSED", pedSign),
  D("sidewalkClosedUseOtherSideLeft", "Sidewalk Closed, Use Other Side (Left)", "R9-10", "Pedestrian / Sidewalk", G("regulatoryRect"), "SIDEWALK CLOSED\n◂ USE OTHER SIDE", pedSign),
  D("sidewalkClosedUseOtherSideRight", "Sidewalk Closed, Use Other Side (Right)", "R9-10", "Pedestrian / Sidewalk", G("regulatoryRect"), "SIDEWALK CLOSED\nUSE OTHER SIDE ▸", pedSign),
  D("sidewalkClosedAheadCrossHereLeft", "Sidewalk Closed Ahead, Cross Here (Left)", "R9-11", "Pedestrian / Sidewalk", G("regulatoryRect"), "SIDEWALK CLOSED\nAHEAD\n◂ CROSS HERE", pedSignTall),
  D("sidewalkClosedAheadCrossHereRight", "Sidewalk Closed Ahead, Cross Here (Right)", "R9-11", "Pedestrian / Sidewalk", G("regulatoryRect"), "SIDEWALK CLOSED\nAHEAD\nCROSS HERE ▸", pedSignTall),
  D("sidewalkClosedCrossHereLeft", "Sidewalk Closed, Cross Here (Left)", "R9-11a", "Pedestrian / Sidewalk", G("regulatoryRect"), "SIDEWALK CLOSED\n◂ CROSS HERE", pedSign),
  D("sidewalkClosedCrossHereRight", "Sidewalk Closed, Cross Here (Right)", "R9-11a", "Pedestrian / Sidewalk", G("regulatoryRect"), "SIDEWALK CLOSED\nCROSS HERE ▸", pedSign),
  D("pedestrianDetourLeft", "Pedestrian Detour (Left)", "M4-9b", "Pedestrian / Sidewalk", G("pedestrianDetour", { arrowRight: false, bike: false }), "DETOUR", detourPanel),
  D("pedestrianDetourRight", "Pedestrian Detour (Right)", "M4-9b", "Pedestrian / Sidewalk", G("pedestrianDetour", { arrowRight: true, bike: false }), "DETOUR", detourPanel),
  D("bikePedDetourLeft", "Bike/Pedestrian Detour (Left)", "M4-9a", "Pedestrian / Sidewalk", G("pedestrianDetour", { arrowRight: false, bike: true }), "DETOUR", detourPanel),
  D("bikePedDetourRight", "Bike/Pedestrian Detour (Right)", "M4-9a", "Pedestrian / Sidewalk", G("pedestrianDetour", { arrowRight: true, bike: true }), "DETOUR", detourPanel),
  D("pedestrianCrossingWarning", "Pedestrian Crossing", "W11-2", "Pedestrian / Sidewalk", G("pedestrianWarning"), "", sign),
  D("crossHereArrowLeft", "Cross Here (Left Arrow)", "—", "Pedestrian / Sidewalk", G("crossHereArrow", { right: false }), "CROSS HERE", [96, 40]),
  D("crossHereArrowRight", "Cross Here (Right Arrow)", "—", "Pedestrian / Sidewalk", G("crossHereArrow", { right: true }), "CROSS HERE", [96, 40]),
  D("temporaryWalkway", "Temporary Walkway", "—", "Pedestrian / Sidewalk", G("guideRect"), "TEMPORARY\nWALKWAY", [96, 48]),
  D("accessibleRoute", "Accessible Pedestrian Route", "—", "Pedestrian / Sidewalk", G("accessiblePanel"), "ACCESSIBLE\nROUTE", [96, 48]),
  D("pedChannelizingDevice", "Pedestrian Channelizing Device (Detectable)", "—", "Pedestrian / Sidewalk", G("pedChannelizer"), "", [110, 32]),
  D("pedBarricadeSidewalkClosed", "Type III Barricade w/ Sidewalk Closed", "R9-9", "Pedestrian / Sidewalk", G("barricadeWithSign"), "SIDEWALK\nCLOSED", [104, 76]),
  D("temporaryCurbRamp", "Temporary Curb Ramp", "—", "Pedestrian / Sidewalk", G("curbRamp"), "", [60, 44]),
  D("detectableWarning", "Detectable Warning Surface", "—", "Pedestrian / Sidewalk", G("detectableWarning"), "", [44, 44]),
];
const BY_SYMBOL = new Map(MOT_LIBRARY.map((d) => [d.symbol, d]));
export const motDefinition = (symbol: string): MOTDefinition | undefined => BY_SYMBOL.get(symbol);

const ORANGE = css(SYSTEM.mutcdOrange!), RED = css(SYSTEM.mutcdRed!), BLACK = "rgba(0, 0, 0, 1)", WHITE = "rgba(255, 255, 255, 1)";
const GRAY = css(SYSTEM.gray!), YELLOW = css(SYSTEM.yellow!);

/** Draws a MOT symbol filling `r` (page space, y up). Unknown symbols get a labelled placeholder. */
export function drawMOT(ctx: Ctx, symbol: string, r: Rect): void {
  const def = motDefinition(symbol);
  if (!def) {
    ctx.strokeStyle = ORANGE; ctx.lineWidth = 1; ctx.setLineDash([4, 3]); ctx.strokeRect(r.x, r.y, r.w, r.h); ctx.setLineDash([]);
    label(ctx, symbol, inset(r, 3, 3), BLACK, false);
    return;
  }
  const legend = def.legend, g = def.glyph;
  switch (g.g) {
    case "warningDiamond": diamond(ctx, r, ORANGE, BLACK); label(ctx, legend, inset(r, r.w * 0.22, r.h * 0.22), BLACK, true); break;
    case "regulatoryRect": panel(ctx, r, WHITE, BLACK, 2); label(ctx, legend, inset(r, 5, 5), BLACK, true); break;
    case "prohibitionRect": panel(ctx, r, WHITE, RED, 3); label(ctx, legend, inset(r, 5, 5), RED, true); break;
    case "stopOctagon": octagon(ctx, r, RED, WHITE); label(ctx, legend, inset(r, r.w * 0.16, r.h * 0.36), WHITE, true); break;
    case "guideRect": panel(ctx, r, ORANGE, BLACK, 2); label(ctx, legend, inset(r, 5, 5), BLACK, true); break;
    case "cone": cone(ctx, r); break;
    case "drum": drum(ctx, r); break;
    case "barricadeX": barricadeX(ctx, r); break;
    case "barricadeRails": barricadeRails(ctx, g.n, r); break;
    case "verticalPanel": stripes(ctx, r); break;
    case "tubularMarker": tubularMarker(ctx, r); break;
    case "delineator": delineator(ctx, r); break;
    case "arrowBoard": arrowBoard(ctx, r, false); break;
    case "arrowBoardCaution": arrowBoard(ctx, r, true); break;
    case "messageBoard": messageBoard(ctx, legend, r); break;
    case "truckAttenuator": truckAttenuator(ctx, r); break;
    case "workVehicle": workVehicle(ctx, r); break;
    case "temporarySignal": temporarySignal(ctx, r); break;
    case "flaggerStation": flaggerStation(ctx, r); break;
    case "pedestrianDetour": pedestrianDetour(ctx, legend, r, g.arrowRight, g.bike); break;
    case "pedestrianWarning":
      diamond(ctx, r, css(SYSTEM.pedYellowGreen!), BLACK);
      pedestrianFigure(ctx, inset(r, r.w * 0.3, r.h * 0.22), BLACK);
      break;
    case "crossHereArrow": {
      panel(ctx, r, WHITE, BLACK, 2);
      const aw = r.w * 0.24;
      const textRect = g.right ? rect(r.x + 6, r.y + 4, r.w - aw - 16, r.h - 8) : rect(r.x + aw + 10, r.y + 4, r.w - aw - 16, r.h - 8);
      label(ctx, legend, textRect, BLACK, true);
      const arrowRect = g.right ? rect(maxX(r) - aw - 4, r.y + r.h * 0.22, aw, r.h * 0.56) : rect(r.x + 4, r.y + r.h * 0.22, aw, r.h * 0.56);
      arrow(ctx, arrowRect, g.right, BLACK);
      break;
    }
    case "accessiblePanel": panel(ctx, r, css(SYSTEM.accessibleBlue!), WHITE, 2); label(ctx, legend, inset(r, 6, 6), WHITE, true); break;
    case "pedChannelizer": pedChannelizer(ctx, r); break;
    case "barricadeWithSign": {
      barricadeRails(ctx, 3, rect(r.x, r.y, r.w, r.h * 0.55));
      const s = rect(midX(r) - r.w * 0.36, r.y + r.h * 0.5, r.w * 0.72, r.h * 0.46);
      panel(ctx, s, WHITE, BLACK, 1.5);
      label(ctx, legend, inset(s, 4, 3), BLACK, true);
      break;
    }
    case "curbRamp": curbRamp(ctx, r); break;
    case "detectableWarning": detectableWarning(ctx, r); break;
  }
}

function fillEllipse(ctx: Ctx, r: Rect) { ctx.beginPath(); ctx.ellipse(midX(r), midY(r), r.w / 2, r.h / 2, 0, 0, Math.PI * 2); ctx.fill(); }
function strokeEllipse(ctx: Ctx, r: Rect) { ctx.beginPath(); ctx.ellipse(midX(r), midY(r), r.w / 2, r.h / 2, 0, 0, Math.PI * 2); ctx.stroke(); }
function poly(ctx: Ctx, pts: [number, number][], close = true) { ctx.beginPath(); pts.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y))); if (close) ctx.closePath(); }

function pedestrianFigure(ctx: Ctx, r: Rect, color: string) {
  ctx.save();
  ctx.strokeStyle = color; ctx.fillStyle = color;
  const w = r.w, h = r.h;
  ctx.lineWidth = Math.max(1.2, w * 0.16); ctx.lineCap = "round"; ctx.lineJoin = "round";
  const headR = Math.min(w, h) * 0.12;
  const head = [midX(r) + w * 0.06, maxY(r) - headR];
  fillEllipse(ctx, rect(head[0]! - headR, head[1]! - headR, headR * 2, headR * 2));
  const neck = [midX(r) + w * 0.02, maxY(r) - h * 0.28], hip = [midX(r) - w * 0.04, r.y + h * 0.38];
  ctx.beginPath();
  ctx.moveTo(neck[0]!, neck[1]!); ctx.lineTo(hip[0]!, hip[1]!);
  ctx.moveTo(neck[0]!, neck[1]! - h * 0.04); ctx.lineTo(r.x + w * 0.1, midY(r) - h * 0.02);
  ctx.moveTo(neck[0]!, neck[1]! - h * 0.04); ctx.lineTo(maxX(r) - w * 0.08, midY(r) + h * 0.02);
  ctx.moveTo(hip[0]!, hip[1]!); ctx.lineTo(r.x + w * 0.08, r.y);
  ctx.moveTo(hip[0]!, hip[1]!); ctx.lineTo(midX(r) + w * 0.1, r.y + h * 0.18); ctx.lineTo(maxX(r) - w * 0.1, r.y);
  ctx.stroke();
  ctx.restore();
}

function bicycle(ctx: Ctx, r: Rect, color: string) {
  ctx.save();
  ctx.strokeStyle = color; ctx.lineWidth = Math.max(1, r.w * 0.06); ctx.lineCap = "round";
  const rr = Math.min(r.w * 0.24, r.h * 0.36);
  const back = [r.x + rr, r.y + rr], front = [maxX(r) - rr, r.y + rr];
  strokeEllipse(ctx, rect(back[0]! - rr, back[1]! - rr, rr * 2, rr * 2));
  strokeEllipse(ctx, rect(front[0]! - rr, front[1]! - rr, rr * 2, rr * 2));
  const crank = [midX(r) - rr * 0.2, back[1]!], seat = [midX(r) - rr * 0.5, r.y + rr * 2.3], bars = [front[0]! - rr * 0.4, r.y + rr * 2.4];
  ctx.beginPath();
  ctx.moveTo(back[0]!, back[1]!); ctx.lineTo(crank[0]!, crank[1]!); ctx.lineTo(seat[0]!, seat[1]!); ctx.lineTo(back[0]!, back[1]!);
  ctx.moveTo(crank[0]!, crank[1]!); ctx.lineTo(bars[0]!, bars[1]!); ctx.lineTo(front[0]!, front[1]!);
  ctx.moveTo(seat[0]!, seat[1]!); ctx.lineTo(bars[0]!, bars[1]!);
  ctx.stroke();
  ctx.restore();
}

function arrow(ctx: Ctx, r: Rect, right: boolean, color: string) {
  const shaft = r.h * 0.34, head = r.w * 0.46, my = midY(r);
  const pts: [number, number][] = right
    ? [[r.x, my - shaft / 2], [maxX(r) - head, my - shaft / 2], [maxX(r) - head, r.y], [maxX(r), my], [maxX(r) - head, maxY(r)], [maxX(r) - head, my + shaft / 2], [r.x, my + shaft / 2]]
    : [[maxX(r), my - shaft / 2], [r.x + head, my - shaft / 2], [r.x + head, r.y], [r.x, my], [r.x + head, maxY(r)], [r.x + head, my + shaft / 2], [maxX(r), my + shaft / 2]];
  poly(ctx, pts); ctx.fillStyle = color; ctx.fill();
}

function pedestrianDetour(ctx: Ctx, legend: string, r: Rect, arrowRight: boolean, bike: boolean) {
  panel(ctx, r, ORANGE, BLACK, 2);
  const inner = inset(r, r.w * 0.08, r.h * 0.07);
  const band = rect(inner.x, inner.y + inner.h * 0.56, inner.w, inner.h * 0.44);
  if (bike) {
    const half = band.w / 2;
    bicycle(ctx, inset(rect(band.x, band.y, half - 2, band.h), 2, 2), BLACK);
    pedestrianFigure(ctx, rect(midX(band) + half * 0.28, band.y, half * 0.44, band.h), BLACK);
  } else {
    pedestrianFigure(ctx, rect(midX(band) - band.w * 0.14, band.y, band.w * 0.28, band.h), BLACK);
  }
  label(ctx, legend, rect(inner.x, inner.y + inner.h * 0.3, inner.w, inner.h * 0.24), BLACK, true);
  arrow(ctx, rect(midX(inner) - inner.w * 0.3, inner.y, inner.w * 0.6, inner.h * 0.26), arrowRight, BLACK);
}

function pedChannelizer(ctx: Ctx, r: Rect) {
  stripes(ctx, rect(r.x, maxY(r) - r.h * 0.3, r.w, r.h * 0.3));
  const bottom = rect(r.x, r.y + r.h * 0.06, r.w, r.h * 0.16);
  ctx.fillStyle = ORANGE; ctx.fillRect(bottom.x, bottom.y, bottom.w, bottom.h);
  ctx.strokeStyle = withAlpha(SYSTEM.black!, 0.4); ctx.lineWidth = 0.7; ctx.strokeRect(bottom.x, bottom.y, bottom.w, bottom.h);
  ctx.fillStyle = GRAY;
  const legW = Math.max(1.5, r.w * 0.03);
  for (const f of [0.08, 0.5, 0.92]) ctx.fillRect(r.x + r.w * f - legW / 2, r.y, legW, r.h * 0.72);
}

function curbRamp(ctx: Ctx, r: Rect) {
  const pts: [number, number][] = [[r.x, r.y], [maxX(r), r.y], [maxX(r) - r.w * 0.18, maxY(r)], [r.x + r.w * 0.18, maxY(r)]];
  poly(ctx, pts); ctx.fillStyle = withAlpha(SYSTEM.gray!, 0.25); ctx.fill();
  poly(ctx, pts); ctx.strokeStyle = BLACK; ctx.lineWidth = 1.2; ctx.stroke();
  detectableWarning(ctx, rect(r.x + r.w * 0.06, r.y + 1, r.w * 0.88, r.h * 0.3));
  ctx.strokeStyle = BLACK; ctx.lineWidth = 1;
  const top = [midX(r), maxY(r) - r.h * 0.1], bot = [midX(r), r.y + r.h * 0.38];
  ctx.beginPath(); ctx.moveTo(top[0]!, top[1]!); ctx.lineTo(bot[0]!, bot[1]!);
  ctx.moveTo(bot[0]! - 3, bot[1]! + 4); ctx.lineTo(bot[0]!, bot[1]!); ctx.lineTo(bot[0]! + 3, bot[1]! + 4);
  ctx.stroke();
}

function detectableWarning(ctx: Ctx, r: Rect) {
  ctx.fillStyle = YELLOW; ctx.fillRect(r.x, r.y, r.w, r.h);
  ctx.strokeStyle = withAlpha(SYSTEM.black!, 0.5); ctx.lineWidth = 0.6; ctx.strokeRect(r.x, r.y, r.w, r.h);
  const pitch = Math.max(3, Math.min(r.w, r.h) / 6), rr = pitch * 0.28;
  ctx.fillStyle = withAlpha(SYSTEM.black!, 0.55);
  for (let y = r.y + pitch / 2; y < maxY(r); y += pitch)
    for (let x = r.x + pitch / 2; x < maxX(r); x += pitch) fillEllipse(ctx, rect(x - rr, y - rr, rr * 2, rr * 2));
}

function diamond(ctx: Ctx, r: Rect, fill: string, border: string) {
  const pts: [number, number][] = [[midX(r), maxY(r)], [maxX(r), midY(r)], [midX(r), r.y], [r.x, midY(r)]];
  poly(ctx, pts); ctx.fillStyle = fill; ctx.fill();
  poly(ctx, pts); ctx.strokeStyle = border; ctx.lineWidth = Math.max(1.5, r.w * 0.03); ctx.stroke();
}

function panel(ctx: Ctx, r: Rect, fill: string, border: string, borderWidth: number) {
  ctx.fillStyle = fill; ctx.fillRect(r.x, r.y, r.w, r.h);
  const s = inset(r, borderWidth / 2, borderWidth / 2);
  ctx.strokeStyle = border; ctx.lineWidth = borderWidth; ctx.strokeRect(s.x, s.y, s.w, s.h);
}

function octagon(ctx: Ctx, r: Rect, fill: string, border: string) {
  const i = Math.min(r.w, r.h) * 0.29;
  const pts: [number, number][] = [[r.x + i, r.y], [maxX(r) - i, r.y], [maxX(r), r.y + i], [maxX(r), maxY(r) - i], [maxX(r) - i, maxY(r)], [r.x + i, maxY(r)], [r.x, maxY(r) - i], [r.x, r.y + i]];
  poly(ctx, pts); ctx.fillStyle = fill; ctx.fill();
  poly(ctx, pts); ctx.strokeStyle = border; ctx.lineWidth = Math.max(1.5, r.w * 0.05); ctx.stroke();
}

function cone(ctx: Ctx, r: Rect) {
  ctx.fillStyle = ORANGE;
  poly(ctx, [[midX(r), maxY(r)], [r.x + r.w * 0.2, r.y + r.h * 0.16], [maxX(r) - r.w * 0.2, r.y + r.h * 0.16]]); ctx.fill();
  ctx.fillStyle = withAlpha(SYSTEM.gray!, 0.55); ctx.fillRect(r.x, r.y, r.w, r.h * 0.1);
  ctx.fillStyle = WHITE; ctx.fillRect(r.x + r.w * 0.3, r.y + r.h * 0.4, r.w * 0.4, r.h * 0.12);
}

function drum(ctx: Ctx, r: Rect) {
  const body = inset(r, r.w * 0.14, r.h * 0.04);
  ctx.fillStyle = ORANGE; ctx.fillRect(body.x, body.y, body.w, body.h);
  ctx.strokeStyle = WHITE; ctx.lineWidth = Math.max(1.4, r.h * 0.07);
  ctx.beginPath();
  for (const f of [0.34, 0.62]) { const y = body.y + body.h * f; ctx.moveTo(body.x, y); ctx.lineTo(maxX(body), y); }
  ctx.stroke();
  ctx.strokeStyle = withAlpha(SYSTEM.black!, 0.25); ctx.lineWidth = 1; ctx.strokeRect(body.x, body.y, body.w, body.h);
}

function barricadeX(ctx: Ctx, r: Rect) {
  ctx.strokeStyle = ORANGE; ctx.lineWidth = Math.max(2, r.h * 0.12);
  ctx.beginPath(); ctx.moveTo(r.x, r.y); ctx.lineTo(maxX(r), maxY(r)); ctx.moveTo(r.x, maxY(r)); ctx.lineTo(maxX(r), r.y); ctx.stroke();
}

function barricadeRails(ctx: Ctx, count: number, r: Rect) {
  const legW = Math.max(2, r.w * 0.05);
  ctx.fillStyle = GRAY;
  ctx.fillRect(r.x + r.w * 0.16, r.y, legW, r.h);
  ctx.fillRect(maxX(r) - r.w * 0.16 - legW, r.y, legW, r.h);
  const railH = r.h * 0.2;
  const gap = count > 1 ? ((r.h - railH) * 0.5) / (count - 1) : 0;
  for (let i = 0; i < count; i++) stripes(ctx, rect(r.x, maxY(r) - railH - i * (railH + gap), r.w, railH));
}

function stripes(ctx: Ctx, r: Rect) {
  ctx.save();
  ctx.fillStyle = WHITE; ctx.fillRect(r.x, r.y, r.w, r.h);
  ctx.beginPath(); ctx.rect(r.x, r.y, r.w, r.h); ctx.clip();
  ctx.fillStyle = ORANGE;
  const band = Math.max(4, r.h);
  for (let x = r.x - r.h; x < maxX(r); x += band * 2) {
    poly(ctx, [[x, r.y], [x + band, r.y], [x + band + r.h, maxY(r)], [x + r.h, maxY(r)]]); ctx.fill();
  }
  ctx.restore();
  ctx.strokeStyle = withAlpha(SYSTEM.black!, 0.3); ctx.lineWidth = 0.7; ctx.strokeRect(r.x, r.y, r.w, r.h);
}

function tubularMarker(ctx: Ctx, r: Rect) {
  const body = rect(midX(r) - r.w * 0.3, r.y, r.w * 0.6, r.h);
  ctx.fillStyle = ORANGE; ctx.fillRect(body.x, body.y, body.w, body.h);
  ctx.fillStyle = WHITE;
  for (const f of [0.55, 0.78]) ctx.fillRect(body.x, body.y + body.h * f, body.w, body.h * 0.1);
}

function delineator(ctx: Ctx, r: Rect) {
  ctx.fillStyle = GRAY; ctx.fillRect(midX(r) - r.w * 0.12, r.y, r.w * 0.24, r.h);
  ctx.fillStyle = ORANGE; ctx.fillRect(r.x, maxY(r) - r.w, r.w, r.w);
}

function arrowBoard(ctx: Ctx, r: Rect, caution: boolean) {
  ctx.fillStyle = BLACK; ctx.fillRect(r.x, r.y, r.w, r.h);
  ctx.strokeStyle = GRAY; ctx.lineWidth = 1; ctx.strokeRect(r.x, r.y, r.w, r.h);
  ctx.fillStyle = ORANGE;
  if (caution) {
    const rr = Math.min(r.w, r.h) * 0.09;
    for (const [cx, cy] of [[r.x + r.w * 0.18, r.y + r.h * 0.28], [maxX(r) - r.w * 0.18, r.y + r.h * 0.28], [r.x + r.w * 0.18, maxY(r) - r.h * 0.28], [maxX(r) - r.w * 0.18, maxY(r) - r.h * 0.28]])
      fillEllipse(ctx, rect(cx! - rr, cy! - rr, rr * 2, rr * 2));
  } else {
    ctx.strokeStyle = ORANGE; ctx.lineWidth = Math.max(2, r.h * 0.1);
    const y = midY(r);
    ctx.beginPath();
    ctx.moveTo(r.x + r.w * 0.16, y); ctx.lineTo(maxX(r) - r.w * 0.28, y); ctx.lineTo(maxX(r) - r.w * 0.44, maxY(r) - r.h * 0.22);
    ctx.moveTo(maxX(r) - r.w * 0.28, y); ctx.lineTo(maxX(r) - r.w * 0.44, r.y + r.h * 0.22);
    ctx.stroke();
  }
}

function messageBoard(ctx: Ctx, legend: string, r: Rect) {
  ctx.fillStyle = BLACK; ctx.fillRect(r.x, r.y, r.w, r.h);
  ctx.strokeStyle = GRAY; ctx.lineWidth = 1; ctx.strokeRect(r.x, r.y, r.w, r.h);
  label(ctx, legend, inset(r, 4, 4), ORANGE, true);
}

function truckAttenuator(ctx: Ctx, r: Rect) {
  ctx.fillStyle = GRAY; ctx.fillRect(r.x, r.y, r.w * 0.34, r.h * 0.7);
  ctx.fillStyle = BLACK;
  const wr = r.h * 0.14;
  for (const cx of [r.x + r.w * 0.12, maxX(r) - r.w * 0.14]) fillEllipse(ctx, rect(cx - wr, r.y - wr * 0.4, wr * 2, wr * 2));
  ctx.strokeStyle = ORANGE; ctx.lineWidth = Math.max(2, r.h * 0.08);
  ctx.beginPath();
  for (let x = r.x + r.w * 0.42; x < maxX(r) - r.w * 0.04; x += r.w * 0.12) {
    ctx.moveTo(x, r.y + r.h * 0.15); ctx.lineTo(x + r.w * 0.1, midY(r)); ctx.lineTo(x, maxY(r) - r.h * 0.15);
  }
  ctx.stroke();
}

function workVehicle(ctx: Ctx, r: Rect) {
  const body = rect(r.x, r.y + r.h * 0.2, r.w, r.h * 0.55);
  ctx.fillStyle = YELLOW; ctx.fillRect(body.x, body.y, body.w, body.h);
  ctx.strokeStyle = BLACK; ctx.lineWidth = 1.2; ctx.strokeRect(body.x, body.y, body.w, body.h);
  ctx.fillStyle = BLACK;
  const wr = r.h * 0.16;
  for (const cx of [r.x + r.w * 0.22, maxX(r) - r.w * 0.22]) fillEllipse(ctx, rect(cx - wr, r.y, wr * 2, wr * 2));
  ctx.fillStyle = ORANGE; ctx.fillRect(midX(r) - r.w * 0.05, maxY(body), r.w * 0.1, r.h * 0.12);
}

function temporarySignal(ctx: Ctx, r: Rect) {
  const head = rect(midX(r) - r.w * 0.28, r.y + r.h * 0.18, r.w * 0.56, r.h * 0.72);
  ctx.fillStyle = BLACK; ctx.fillRect(head.x, head.y, head.w, head.h);
  ctx.fillStyle = GRAY; ctx.fillRect(midX(r) - r.w * 0.05, r.y, r.w * 0.1, r.h * 0.2);
  const d = head.w * 0.62;
  [SYSTEM.red!, SYSTEM.yellow!, SYSTEM.green!].forEach((c, i) => {
    const cy = maxY(head) - head.h * (0.22 + 0.28 * i);
    ctx.fillStyle = css(c);
    fillEllipse(ctx, rect(midX(head) - d / 2, cy - d / 2, d, d));
  });
}

function flaggerStation(ctx: Ctx, r: Rect) {
  ctx.strokeStyle = BLACK; ctx.fillStyle = BLACK; ctx.lineWidth = Math.max(1.5, r.w * 0.06);
  const hr = r.w * 0.14, hc = [midX(r) - r.w * 0.08, maxY(r) - hr];
  fillEllipse(ctx, rect(hc[0]! - hr, hc[1]! - hr, hr * 2, hr * 2));
  ctx.beginPath(); ctx.moveTo(hc[0]!, hc[1]! - hr); ctx.lineTo(hc[0]!, r.y + r.h * 0.22); ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(hc[0]!, r.y + r.h * 0.22); ctx.lineTo(hc[0]! - r.w * 0.14, r.y);
  ctx.moveTo(hc[0]!, r.y + r.h * 0.22); ctx.lineTo(hc[0]! + r.w * 0.14, r.y);
  ctx.stroke();
  ctx.beginPath(); ctx.moveTo(hc[0]!, midY(r) + r.h * 0.08); ctx.lineTo(maxX(r) - r.w * 0.18, midY(r) + r.h * 0.16); ctx.stroke();
  ctx.fillStyle = ORANGE; fillEllipse(ctx, rect(maxX(r) - r.w * 0.28, midY(r) + r.h * 0.06, r.w * 0.24, r.w * 0.24));
}

/** MOTGlyphRenderer.label: 0.70 em per bold cap, 1.16 line height. */
function label(ctx: Ctx, text: string, r: Rect, color: string, bold: boolean) {
  if (!text) return;
  const lines = text.split("\n");
  const longest = Math.max(1, ...lines.map((l) => [...l].length));
  const fontSize = Math.max(4.5, Math.min(r.w / (longest * 0.7), r.h / (lines.length * 1.2)));
  ctx.font = `${bold ? "bold " : ""}${+fontSize.toFixed(3)}px Helvetica, Arial, 'Liberation Sans', sans-serif`;
  ctx.fillStyle = color;
  const lh = fontSize * 1.16;
  const first = midY(r) + ((lines.length - 1) * lh) / 2 - fontSize * 0.34;
  lines.forEach((l, i) => textAt(ctx, l, midX(r) - ctx.measureText(l).width / 2, first - i * lh));
}
